import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for infinite scroll functionality
 * Uses Intersection Observer API for optimal performance
 * 
 * @param {Function} callback - Function to call when sentinel is intersected
 * @param {boolean} hasMore - Whether there are more items to load
 * @param {boolean} isLoading - Whether items are currently loading
 * @param {Array} dependencies - Additional dependencies for the callback
 * @returns {Object} sentinelRef - Ref to attach to the sentinel element
 */
const useInfiniteScroll = (callback, hasMore, isLoading = false, dependencies = []) => {
    const sentinelRef = useRef(null);
    const observerRef = useRef(null);

    const handleIntersection = useCallback((entries) => {
        const [entry] = entries;

        // Only load more if:
        // 1. Sentinel is intersecting (visible in viewport)
        // 2. Not currently loading
        // 3. There are more items to load
        if (entry.isIntersecting && !isLoading && hasMore) {
            callback();
        }
    }, [callback, isLoading, hasMore, ...dependencies]);

    useEffect(() => {
        const sentinel = sentinelRef.current;

        if (!sentinel) return;

        // Create intersection observer with some threshold
        observerRef.current = new IntersectionObserver(handleIntersection, {
            root: null, // viewport
            rootMargin: '100px', // Start loading 100px before reaching the sentinel
            threshold: 0.1
        });

        observerRef.current.observe(sentinel);

        // Cleanup
        return () => {
            if (observerRef.current && sentinel) {
                observerRef.current.unobserve(sentinel);
            }
        };
    }, [handleIntersection]);

    return sentinelRef;
};

export default useInfiniteScroll;
